import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SINGLE_HOLDER_JUSTIFICATION_MIN } from '@loot/shared';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, type SingleHolderRelease, type SingleHolderStatus } from '../api.ts';
import { loginUrl, ME_KEY } from '../auth.tsx';
import { localized } from '../i18n/index.ts';
import { MasterDeleteButton } from '../components/MasterDelete.tsx';
import {
  Badge,
  Button,
  EmptyRow,
  ErrorBox,
  Field,
  Loading,
  PageHeader,
  Panel,
  Table,
  Textarea,
} from '../components/ui.tsx';

const PATH = '/single-holder';

/**
 * Liberação de responsável único (ADR 0043): enquanto só há um responsável, ele libera para si o
 * perfil de dados pessoais do DP, com justificativa, segunda etapa recente, carência de 24 horas e
 * prazo. O banco confere tudo; a tela só explica e conduz.
 */
export function SingleHolderReleasePage() {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const [justification, setJustification] = useState('');
  const status = useQuery({
    queryKey: ['single-holder'],
    queryFn: () => api.get<SingleHolderStatus>('/iam/single-holder'),
  });
  const done = async () => {
    await qc.invalidateQueries({ queryKey: ['single-holder'] });
    await qc.invalidateQueries({ queryKey: ME_KEY });
    await qc.invalidateQueries({ queryKey: ['users'] });
  };
  const request = useMutation({
    mutationFn: () => api.post('/iam/single-holder/requests', { justification }),
    onSuccess: async () => {
      setJustification('');
      await done();
    },
  });
  const action = useMutation({
    mutationFn: ({ id, kind }: { id: string; kind: 'cancel' | 'activate' | 'withdraw' }) =>
      api.post(`/iam/single-holder/requests/${id}/${kind}`, {}),
    onSuccess: done,
  });
  const dateTime = new Intl.DateTimeFormat(i18n.language, {
    dateStyle: 'short',
    timeStyle: 'short',
  });
  const fmt = (v: string | null) => (v ? dateTime.format(new Date(v)) : '');

  if (status.isPending) return <Loading />;
  if (status.isError) return <ErrorBox error={status.error} />;
  const s = status.data;
  const open = s.releases.find((r) => r.status === 'pending' || r.status === 'active');
  const tooShort = justification.trim().length < SINGLE_HOLDER_JUSTIFICATION_MIN;

  const statusBadge = (r: SingleHolderRelease) => {
    const tone = r.status === 'active' ? 'ok' : r.status === 'pending' ? 'info' : 'off';
    return <Badge tone={tone}>{t(`singleHolder.status.${r.status}`)}</Badge>;
  };
  const when = (r: SingleHolderRelease) => {
    if (r.status === 'pending') return t('singleHolder.from', { date: fmt(r.effectiveFrom) });
    if (r.status === 'active') return t('singleHolder.until', { date: fmt(r.expiresAt) });
    return t('singleHolder.endedAt', { date: fmt(r.endedAt) });
  };

  return (
    <>
      <PageHeader title={t('singleHolder.title')} description={t('singleHolder.description')} />
      <ErrorBox error={request.error ?? action.error} />

      <Panel title={t('singleHolder.situation')}>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          {s.eligible ? (
            <Badge tone="ok">{t('singleHolder.eligible')}</Badge>
          ) : (
            <Badge tone="off">{t('singleHolder.notEligible')}</Badge>
          )}
          {s.reason && <span>{t(`singleHolder.reasons.${s.reason}`)}</span>}
        </div>
        {s.policy && (
          <ul className="list-disc pl-5 text-sm text-muted">
            <li>
              {t('singleHolder.rules.role', {
                role: localized(s.policy.roleName),
                code: s.policy.roleCode,
              })}
            </li>
            <li>{t('singleHolder.rules.grace', { hours: s.policy.graceHours })}</li>
            <li>{t('singleHolder.rules.activation', { days: s.policy.activationDays })}</li>
            <li>{t('singleHolder.rules.validity', { days: s.policy.validityDays })}</li>
            <li>{t('singleHolder.rules.reauth', { minutes: s.policy.reauthMinutes })}</li>
            <li>{t('singleHolder.rules.end')}</li>
          </ul>
        )}
        <div className="flex flex-wrap items-center gap-3 text-sm">
          {s.strongAuth ? (
            <Badge tone="ok">{t('singleHolder.strongAuthOk')}</Badge>
          ) : (
            <>
              <Badge tone="off">{t('singleHolder.strongAuthMissing')}</Badge>
              <a className="text-accent hover:underline" href={`${loginUrl(PATH)}&reauth=1`}>
                {t('singleHolder.reauth')}
              </a>
            </>
          )}
        </div>
      </Panel>

      {s.eligible && !s.releases.some((r) => r.status === 'pending') && (
        <Panel title={open ? t('singleHolder.renewTitle') : t('singleHolder.requestTitle')}>
          <Field
            label={t('singleHolder.justification')}
            hint={t('singleHolder.justificationHint', {
              count: justification.trim().length,
              min: SINGLE_HOLDER_JUSTIFICATION_MIN,
            })}
            required
          >
            <Textarea
              value={justification}
              maxLength={1000}
              onChange={(e) => setJustification(e.target.value)}
            />
          </Field>
          <div>
            <Button
              disabled={tooShort || !s.strongAuth || request.isPending}
              onClick={() => {
                if (window.confirm(t('singleHolder.requestConfirm'))) request.mutate();
              }}
            >
              {t('singleHolder.request')}
            </Button>
          </div>
        </Panel>
      )}

      <Table
        head={[
          t('singleHolder.requestedAt'),
          t('fields.status'),
          t('singleHolder.dates'),
          t('singleHolder.justification'),
          '',
        ]}
      >
        {s.releases.length === 0 && <EmptyRow colSpan={5}>{t('singleHolder.empty')}</EmptyRow>}
        {s.releases.map((r) => (
          <tr key={r.id}>
            <td className="text-xs whitespace-nowrap">{fmt(r.requestedAt)}</td>
            <td>
              {statusBadge(r)}
              {r.endReason && (
                <div className="mt-1 text-xs text-muted">
                  {t(`singleHolder.endReasons.${r.endReason}`, { defaultValue: r.endReason })}
                </div>
              )}
            </td>
            <td className="text-xs">{when(r)}</td>
            <td className="max-w-xs text-xs">{r.justification}</td>
            <td className="text-right">
              <div className="flex justify-end gap-2 whitespace-nowrap">
                {r.canActivate && (
                  <Button
                    disabled={action.isPending || !s.strongAuth}
                    onClick={() => {
                      if (window.confirm(t('singleHolder.activateConfirm')))
                        action.mutate({ id: r.id, kind: 'activate' });
                    }}
                  >
                    {t('singleHolder.activate')}
                  </Button>
                )}
                {r.canCancel && (
                  <Button
                    variant="secondary"
                    disabled={action.isPending}
                    onClick={() => action.mutate({ id: r.id, kind: 'cancel' })}
                  >
                    {t('singleHolder.cancel')}
                  </Button>
                )}
                {r.canWithdraw && (
                  <Button
                    variant="danger"
                    disabled={action.isPending}
                    onClick={() => {
                      if (window.confirm(t('singleHolder.withdrawConfirm')))
                        action.mutate({ id: r.id, kind: 'withdraw' });
                    }}
                  >
                    {t('singleHolder.withdraw')}
                  </Button>
                )}
                <MasterDeleteButton table="iam.single_holder_release" id={r.id} compact />
              </div>
            </td>
          </tr>
        ))}
      </Table>
    </>
  );
}
